// The spin pipeline (docs/04 §2): PRE → STOPS → FATE → REVEAL → LAND → EFFECTS → PATTERNS → SCORE
// → TRIGGERS → POST, plus Nudge (§7) and banking.
import { B } from '../content/balance';
import type { GridCellView, LandCtx } from '../content/defs';
import { CHARMS, SYMBOL_LIST, SYMBOLS } from '../content/registry';
import { startBonus } from './bonus/index';
import { D, type Decimal } from './decimal';
import { say, tip } from './dialogue';
import { makeGridView } from './effects';
import { columnDefs, deckIndex, dims, insertIntoStrips, mod, newInstance, passiveReel, removeFromStrips, visibleUids } from './grid';
import { applyDevour, noticeEyes, setDoom } from './doom';
import { applyRandomHex, cleanse, removeOneHex, tickHexes } from './hex';
import { hungerActive, hungerTick, moodLuck, moodOf, moodSpinTick, pickMeal, setHunger, setTempMood } from './hunger';
import { activeCharms, computeModifiers, type Modifiers } from './modifiers';
import { matchCells } from './patterns';
import { hexEachSpin } from './reckoning';
import { rotCheck } from './rot';
import { peekStream, stream } from './rng';
import { evaluate, winTier, tierAtLeast, type EvalOpts, type Evaluation } from './scoring';
import { par } from './tribute';
import type { BonusMode, Cell, GameEvent, GameState, RunPhase, RunState, SpinSnapshot, SymbolInstance } from './types';
import { afterSpin } from './window';

export function currentPar(run: RunState): Decimal {
  return par(run.window.tribute, run.window.spinsTotal);
}

/** Luck for a spin, before the Fate cap. */
export function spinLuck(state: GameState, mods: Modifiers, perfect: boolean, initial: Evaluation): number {
  const run = state.run!;
  let luck = mods.luck + run.luckTemp;
  if (perfect) luck += mods.perfectPullLuck;
  for (const v of initial.view.all()) if (v.def.luck) luck += v.def.luck + (mods.luckById[v.def.id] ?? 0);
  luck += pityLuck(run.deadStreak, mods.pityAfter);
  luck += Math.floor((run.window.n - 1) / 5) * mods.favoredLuckPer5;
  luck += moodLuck(run, mods);
  for (const id of activeCharms(run, mods.charmSlots)) luck += CHARMS.get(id)?.spinLuck?.({ spinOfWindow: run.window.spinsUsed - 1 }) ?? 0;
  return Math.max(0, luck);
}

export function pityLuck(deadStreak: number, after: number): number {
  return deadStreak >= after ? B.PITY_BASE_LUCK + (deadStreak - after) * B.PITY_STEP : 0;
}

function evalOpts(run: RunState, mods: Modifiers, held: boolean[], perfect: boolean, luck: number): EvalOpts {
  return { spinIndex: run.spinIndex, held, perfect, luckOverCap: Math.max(0, luck - mods.fateCap) };
}

/** Reels that have at least one cell outside every paying pattern (Fate may touch them). */
function fateCandidates(run: RunState, ev: Evaluation, held: boolean[]): number[] {
  const inHit = new Set<string>();
  for (const h of ev.hits) for (const c of h.cells) inHit.add(`${c.reel},${c.row}`);
  const out: number[] = [];
  ev.uids.forEach((col, reel) => {
    if (held[reel] || passiveReel(run, reel)) return;
    if (col.some((_, row) => !inHit.has(`${reel},${row}`))) out.push(reel);
  });
  return out;
}

function anticipation(ev: Evaluation, reel: number, mods: Modifiers): boolean {
  if (reel === 0) return false;
  let sigils = 0;
  for (let x = 0; x < reel; x++)
    for (let y = 0; y < ev.view.rows; y++) if (ev.view.at(x, y)?.def.sigil) sigils++;
  if (sigils >= B.SIGILS_TO_TRIGGER - 1) return true;
  if (reel !== ev.view.reels - 1 || reel < 2) return false;
  for (let y = 0; y < ev.view.rows; y++) {
    const cells: GridCellView[] = [];
    for (let x = 0; x < reel; x++) {
      const c = ev.view.at(x, y);
      if (c) cells.push(c);
    }
    if (cells.length === reel && matchCells(cells, mods)) return true;
  }
  return false;
}

/** Nudges that would turn a dead spin into a win. Only worth knowing when a nudge is possible. */
function nearMisses(run: RunState, mods: Modifiers, stops: number[], opts: EvalOpts, current: Decimal): SpinSnapshot['nearMiss'] {
  if (current.gt(0) || !mods.features.has('nudge') || run.charges.nudge <= 0) return [];
  const out: SpinSnapshot['nearMiss'] = [];
  for (let reel = 0; reel < stops.length; reel++) {
    if (passiveReel(run, reel) || mods.lockedReels.has(reel)) continue;
    for (const dir of [1, -1] as const) {
      const trial = [...stops];
      trial[reel] = mod(trial[reel]! - dir, run.strips[reel]!.length);
      if (evaluate(run, mods, trial, opts).payout.gt(0)) out.push({ reel, dir });
    }
  }
  return out;
}

function snapshotOf(run: RunState, ev: Evaluation, stops: number[], perfect: boolean, held: boolean[], luck: number, nudges: number, nearMiss: SpinSnapshot['nearMiss']): SpinSnapshot {
  return {
    index: run.spinIndex,
    stops: [...stops],
    grid: ev.grid,
    uids: ev.uids,
    values: ev.values,
    hits: ev.hits,
    off: ev.off,
    xMults: ev.xMults,
    payout: ev.payout,
    tier: winTier(ev.payout, currentPar(run), ev.gorge),
    perfect,
    held,
    luck,
    nudges,
    banked: false,
    nearMiss,
    ...(ev.devour.kind !== 'none' ? { devour: ev.devour.kind } : {}),
  };
}

function ageOf(mods: Modifiers, v: GridCellView): number {
  let n = 1 + (mods.ageExtraById[v.def.id] ?? 0);
  for (const t of v.def.tags) n += mods.ageExtraByTag[t] ?? 0;
  return n;
}

/** Step 4: LAND — ages and on-land hooks; applied once per real spin. */
function land(state: GameState, mods: Modifiers, stops: number[], events: GameEvent[]): void {
  const run = state.run!;
  const { rows } = dims(run.size);
  const uids = visibleUids(run, stops);
  const aging = makeGridView(uids, deckIndex(run), rows)
    .all()
    .filter((v) => v.inst.uid >= 0);
  for (const v of aging) v.inst.age += ageOf(mods, v);
  const rng = stream(run.rng, 'events');
  rotCheck(aging, mods, rng, events);
  // Rebuilt so on-land hooks see this spin's rot.
  const view = makeGridView(uids, deckIndex(run), rows);
  const all = view.all().filter((v) => v.inst.uid >= 0);
  const cellOf = (inst: SymbolInstance, fallback: Cell): Cell => all.find((x) => x.inst === inst)?.cell ?? fallback;
  // Defs are fixed before any hook runs: a symbol transformed this spin doesn't also land as its new self.
  const landing = all.map((v) => SYMBOLS.get(v.inst.defId));
  for (const [i, v] of all.entries()) {
    const def = landing[i];
    if (!def?.land) continue;
    const self = { ...v, def };
    const ctx: LandCtx = {
      self,
      grid: view,
      windowN: run.window.n,
      rng,
      mods,
      applyHex: (by) => applyRandomHex(state, mods, rng, by, events),
      removeHex: (by) => removeOneHex(state, by, events),
      addDoom: (n) => setDoom(state, mods, run.doom + n, events),
      addSpins: (n, by, cap) => {
        const used = run.window.counters[cap.key] ?? 0;
        const add = Math.min(n, cap.max - used);
        if (add <= 0) return;
        run.window.counters[cap.key] = used + add;
        run.window.spinsLeft += add;
        run.window.spinsTotal += add;
        events.push({ e: 'SPINS_ADDED', n: add, by });
      },
      freeHold: (reel) => void (run.freeHold[reel] = true),
      transform: (inst, to) => {
        if (inst.defId === to) return;
        const from = inst.defId;
        inst.defId = to;
        inst.age = 0;
        events.push({ e: 'SYMBOL_TRANSFORMED', uid: inst.uid, cell: cellOf(inst, v.cell), from, to });
      },
      transformRandom: (inst, filter) => {
        const pool = SYMBOL_LIST.filter(
          (d) =>
            d.rarity &&
            !d.sigil &&
            !d.noDraft &&
            d.id !== inst.defId &&
            mods.symbolGates.has(d.gate) &&
            (!filter.rarity || d.rarity === filter.rarity) &&
            (!filter.tag || d.tags.includes(filter.tag)),
        );
        if (pool.length) ctx.transform(inst, rng.pick(pool).id);
      },
      grow: (inst, n) => {
        inst.bonusValue += n;
        events.push({ e: 'SYMBOL_EFFECT', cell: cellOf(inst, v.cell), kind: 'grow', delta: n });
      },
      ageBy: (inst, n) => void (inst.age += n),
    };
    def.land(ctx);
  }
  noticeEyes(state, mods, makeGridView(uids, deckIndex(run), rows), events);
}

/** Step 4 (cont.): a RAVENOUS/STARVING machine eats (docs/04 §12); so does the Glutton (§16). */
function machineEats(state: GameState, mods: Modifiers, stops: number[], events: GameEvent[]): void {
  const run = state.run!;
  const hungry = hungerActive(mods);
  if (!hungry && !mods.gluttonEats) return;
  const ev = evaluate(run, mods, stops, evalOpts(run, mods, run.held, false, 0));
  const cands = () =>
    ev.view
      .all()
      .filter((v) => v.inst.uid >= 0 && !ev.guarded.has(v.inst.uid) && !mods.noEat.has(v.def.id) && !run.eaten.includes(v.inst.uid))
      .map((v) => ({ uid: v.inst.uid, cell: v.cell, value: ev.values[v.cell.reel]![v.cell.row]! }));
  const eat = (uid: number, by: 'maw' | 'glutton') => {
    run.eaten.push(uid);
    const cells: Cell[] = [];
    ev.uids.forEach((col, reel) => col.forEach((u, row) => u === uid && cells.push({ reel, row })));
    const defId = run.deck.find((d) => d.uid === uid)?.defId ?? 'cherry';
    events.push({ e: 'SYMBOL_EATEN', uid, cells, defId, mood: moodOf(run, mods), by });
  };
  const rng = stream(run.rng, 'events');
  if (hungry) {
    const meal = pickMeal(run, mods, cands(), rng);
    if (meal) {
      eat(meal.uid, 'maw');
      say(state, 'eat', events);
    }
  }
  if (mods.gluttonEats && run.deck.length - run.eaten.length > B.MIN_DECK) {
    const left = cands();
    if (left.length) eat(rng.pick(left).uid, 'glutton');
  }
}

export function pull(state: GameState, modsIn: Modifiers, perfectInput: boolean, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run) return 'no run';
  if (run.phase === 'nudge') {
    bank(state, modsIn, events);
    if ((run.phase as RunPhase) !== 'ready') return undefined;
  }
  if (run.phase !== 'ready') return 'not ready';
  const night = (run.nightShift?.left ?? 0) > 0;
  if (run.window.spinsLeft <= 0 && !night) return 'no spins';

  // 0. PRE
  run.eaten = [];
  if (!night) {
    run.window.spinsLeft--;
    run.window.spinsUsed++;
  }
  run.spinIndex++;
  if (night) run.flags.nightSpin = run.spinIndex;
  run.stats.spins++;
  state.meta.lifetime.spins++;
  const perfect = perfectInput || modsIn.perfectPullAnyInput || state.meta.settings.muscleMemory;
  if (perfect) {
    run.stats.perfectPulls++;
    state.meta.lifetime.perfectPulls++;
  }
  if (modsIn.spinCoinCost > 0 && run.coins.gt(0)) run.coins = run.coins.sub(run.coins.mul(modsIn.spinCoinCost).floor());
  if (hexEachSpin(run) && modsIn.symbolGates.has('hex')) applyRandomHex(state, modsIn, stream(run.rng, 'events'), 'widow', events);
  // Rules and hexes applied at PRE shape this very spin.
  let mods = computeModifiers(state.meta, run);
  const held = run.held.map((h, i) => (h || !!run.freeHold[i]) && !passiveReel(run, i));
  run.freeHold = run.freeHold.map(() => false);

  // 1. STOPS
  const reels = stream(run.rng, 'reels');
  let stops = run.stops.map((s, i) => (held[i] ? s : reels.int(run.strips[i]!.length)));

  // 2. FATE
  const initial = evaluate(run, mods, stops, evalOpts(run, mods, held, perfect, 0));
  const luck = spinLuck(state, mods, perfect, initial);
  const opts = evalOpts(run, mods, held, perfect, luck);
  let ev = evaluate(run, mods, stops, opts);
  events.push({ e: 'SPIN_START', index: run.spinIndex, held, perfect, luck });
  const pity = pityLuck(run.deadStreak, mods.pityAfter);
  if (pity > 0) events.push({ e: 'PITY', luck: pity });
  stops.forEach((stop, reel) =>
    events.push({ e: 'REEL_STOP', reel, stop, column: columnDefs(run, reel, stop, mods.peekRows, stops), anticipation: anticipation(ev, reel, mods) }),
  );

  const fate = stream(run.rng, 'fate');
  const passes = Math.min(luck, mods.fateCap);
  for (let i = 0; i < passes; i++) {
    const cands = fateCandidates(run, ev, held);
    if (cands.length === 0) break;
    const reel = fate.pick(cands);
    const to = fate.int(run.strips[reel]!.length);
    if (to === stops[reel]) continue;
    const trial = [...stops];
    trial[reel] = to;
    const tev = evaluate(run, mods, trial, opts);
    const better = tev.payout.gt(ev.payout) || (mods.loadedFate && tev.payout.eq(ev.payout) && tev.hits.length > ev.hits.length);
    if (!better) continue;
    events.push({ e: 'FATE_PASS', reel, from: stops[reel]!, to, column: columnDefs(run, reel, to, mods.peekRows, trial) });
    stops = trial;
    ev = tev;
  }

  if (ev.payout.lte(0) && mods.secondChance && !run.window.counters.secondChance) {
    run.window.counters.secondChance = 1;
    stops = stops.map((s, i) => (held[i] ? s : fate.int(run.strips[i]!.length)));
    events.push({ e: 'SECOND_CHANCE', stops: [...stops], columns: stops.map((s, reel) => columnDefs(run, reel, s, mods.peekRows, stops)) });
  }
  run.stops = stops;

  // 3–4. REVEAL + LAND (permanent, once); the machine and the Glutton eat.
  land(state, mods, stops, events);
  mods = computeModifiers(state.meta, run);
  machineEats(state, mods, stops, events);

  // 5–7. EFFECTS, PATTERNS, SCORE on the post-land grid
  const opts2 = evalOpts(run, mods, held, perfect, luck);
  ev = evaluate(run, mods, run.stops, opts2);
  run.spin = snapshotOf(run, ev, run.stops, perfect, held, luck, 0, nearMisses(run, mods, run.stops, opts2, ev.payout));
  events.push({ e: 'SPIN_RESOLVED', snapshot: run.spin, par: currentPar(run).toString() });
  if (run.stats.spins === 1) say(state, 'firstSpin', events);
  if (run.spin.nearMiss.length && run.charges.nudge > 0 && mods.features.has('nudge')) say(state, 'nearMiss', events);

  run.held = run.held.map(() => false);
  run.holdPaid = run.held.map(() => 0);

  // 10. POST: open the nudge phase or bank right away.
  if (mods.features.has('nudge') && run.charges.nudge > 0) {
    run.phase = 'nudge';
    events.push({ e: 'NUDGE_OPEN', charges: run.charges.nudge });
    tip(state, 'nudge', events);
  } else {
    bank(state, mods, events);
  }
  return undefined;
}

/** Crystal Ball: where reel 1 will stop on the next pull (null if it is held). Pure: reads a clone. */
export function crystalBallStop(run: RunState): number | null {
  if (run.phase !== 'ready' || !run.strips[0]?.length || run.held[0] || run.freeHold[0]) return null;
  return peekStream(run.rng, 'reels').int(run.strips[0].length);
}

/** Nudge preview: the exact evaluation a NUDGE would produce (docs/04 §7). Pure. */
export function previewNudge(state: GameState, mods: Modifiers, reel: number, dir: 1 | -1, steps = 1): Evaluation | null {
  const run = state.run;
  if (!run?.spin || run.phase !== 'nudge') return null;
  // A locked reel (a hex, The Mirror Man's passive half) can't be nudged, so it has no preview.
  if (mods.lockedReels.has(reel) || passiveReel(run, reel)) return null;
  const d = mods.invertNudge ? -dir : dir;
  const stops = [...run.stops];
  stops[reel] = mod(stops[reel]! - d * steps, run.strips[reel]!.length);
  return evaluate(run, mods, stops, evalOpts(run, mods, run.spin.held, run.spin.perfect, run.spin.luck));
}

export function nudge(state: GameState, mods: Modifiers, reel: number, dir: 1 | -1, steps: 1 | 2, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'nudge' || !run.spin) return 'not nudging';
  if (reel < 0 || reel >= run.stops.length) return 'bad reel';
  if (mods.lockedReels.has(reel) || passiveReel(run, reel)) return 'reel locked';
  if (steps === 2 && !mods.longNudge) return 'no long nudge';
  if (run.charges.nudge <= 0) return 'no charges';
  const ev = previewNudge(state, mods, reel, dir, steps)!;
  const free = mods.freeNudgeChance > 0 && stream(run.rng, 'events').chance(mods.freeNudgeChance);
  if (!free) run.charges.nudge--;
  if (mods.nudgeCoinCost > 0 && run.coins.gt(0)) run.coins = run.coins.sub(run.coins.mul(mods.nudgeCoinCost).floor());
  run.stats.nudgesUsed++;
  run.window.counters.nudges = (run.window.counters.nudges ?? 0) + 1;
  const d = mods.invertNudge ? -dir : dir;
  run.stops[reel] = mod(run.stops[reel]! - d * steps, run.strips[reel]!.length);
  const s = run.spin;
  const opts = evalOpts(run, mods, s.held, s.perfect, s.luck);
  const wasWin = s.payout.gt(0);
  run.spin = snapshotOf(run, ev, run.stops, s.perfect, s.held, s.luck, s.nudges + 1, nearMisses(run, mods, run.stops, opts, ev.payout));
  if (!wasWin && ev.payout.gt(0)) run.flags.nudgeWins = (run.flags.nudgeWins ?? 0) + 1;
  const peek = mods.peekRows;
  events.push({ e: 'NUDGED', reel, dir: d as 1 | -1, steps, stop: run.stops[reel]!, column: columnDefs(run, reel, run.stops[reel]!, peek, run.stops), free });
  events.push({ e: 'SPIN_RESOLVED', snapshot: run.spin, par: currentPar(run).toString() });
  events.push({ e: 'CHARGES', hold: run.charges.hold, nudge: run.charges.nudge });
  if ((run.window.counters.nudges ?? 0) >= B.SULK_NUDGES) {
    if (hungerActive(mods) && run.mood.temp !== 'sulking') setTempMood(state, mods, 'sulking', -1, events);
    say(state, 'sulk', events);
  } else say(state, 'nudge', events);
  if (run.charges.nudge <= 0) bank(state, mods, events);
  return undefined;
}

/** Bank the current spin: apply queued permanent effects, pay coins, triggers (steps 8–10). */
export function bank(state: GameState, mods: Modifiers, events: GameEvent[]): void {
  const run = state.run!;
  const s = run.spin;
  if (!s || s.banked) return;
  s.banked = true;
  const ev = evaluate(run, mods, run.stops, evalOpts(run, mods, s.held, s.perfect, s.luck));

  let hunger = run.hunger - applyDevour(state, mods, ev.devour, events);
  const rng = stream(run.rng, 'events');
  let cleansed = false;
  for (const p of ev.pending) {
    if (p.kind === 'freeCharm') run.freeCharms++;
    else if (p.kind === 'cleanse') cleansed = true;
    else if (p.kind === 'grow') {
      const inst = run.deck.find((d) => d.uid === p.uid);
      if (inst) inst.bonusValue += p.n;
    } else if (p.kind === 'hunger') hunger += p.n;
    else if (p.kind === 'spawn') {
      if (!rng.chance(p.chance)) continue;
      const inst = newInstance(run, p.defId);
      run.deck.push(inst);
      insertIntoStrips(run, inst.uid, stream(run.rng, 'reels'));
      events.push({ e: 'SYMBOL_ADDED', uid: inst.uid, defId: p.defId, by: p.by });
    } else if (p.kind === 'destroy') {
      if (destroy(run, p.uid, p.by, events)) hunger -= B.HUNGER_CRUMBS;
    }
  }
  // The machine's own meals: no crumbs, it *is* the meal.
  for (const uid of run.eaten) {
    if (run.deck.length <= B.MIN_DECK) break;
    if (destroy(run, uid, 'maw', events)) run.window.counters.eats = (run.window.counters.eats ?? 0) + 1;
  }
  run.eaten = [];

  const payout = s.payout;
  for (const id of activeCharms(run, mods.charmSlots)) {
    const def = CHARMS.get(id);
    def?.onBank?.({
      payout,
      store: (key, amount) => {
        const k = `${id}.${key}`;
        run.charmBank[k] = (run.charmBank[k] ?? D(0)).add(amount);
      },
    });
  }
  run.coins = run.coins.add(payout);
  events.push({ e: 'COINS_BANKED', amount: payout.toString(), total: run.coins.toString() });

  if (payout.gt(0)) {
    run.stats.wins++;
    if (payout.gt(run.stats.bestWin)) run.stats.bestWin = payout;
    run.deadStreak = 0;
    run.luckTemp = mods.hotHandLuck;
  } else {
    run.deadStreak++;
    run.luckTemp = 0;
    if (mods.features.has('nudge')) {
      let refund = mods.deadSpinNudge;
      if (mods.deadSpinNudgeRefund > 0 && !run.window.counters.reflex) {
        run.window.counters.reflex = 1;
        refund += mods.deadSpinNudgeRefund;
      }
      if (refund > 0) {
        run.charges.nudge += refund;
        events.push({ e: 'CHARGES', hold: run.charges.hold, nudge: run.charges.nudge });
      }
    }
  }
  if (cleansed) cleanse(state, events);
  tickHexes(state, events);

  const tier = s.tier;
  // 9. TRIGGERS: hunger tick, AMUSED after an Epic+ win.
  setHunger(state, mods, hunger, events);
  hungerTick(state, mods, events);
  moodSpinTick(state, mods, events);
  if (tierAtLeast(tier, 'epic')) setTempMood(state, mods, 'amused', B.AMUSED_SPINS, events);
  if (tier === 'gorge') say(state, 'gorge', events);
  else if (tierAtLeast(tier, 'epic')) say(state, 'epic', events);
  else if (tier === 'big' || tier === 'mega') say(state, 'big', events);
  else if (tier === 'win') say(state, 'small', events);
  else if (!s.nearMiss.length) say(state, 'dead', events);

  // Night Shift: the free spin is spent and the wild column walks one reel right.
  const nightSpin = run.flags.nightSpin === run.spinIndex && !!run.nightShift;
  if (nightSpin && run.nightShift) {
    run.nightShift.left--;
    run.nightShift.wild = (run.nightShift.wild + 1) % dims(run.size).reels;
    const granted = run.window.counters.nightRetrigger ?? 0;
    const retrigger = (ev.sigils.wheel ?? 0) >= B.SIGILS_TO_TRIGGER && granted < B.NIGHT_SHIFT_RETRIGGER_CAP;
    if (retrigger) {
      run.nightShift.left += B.NIGHT_SHIFT_RETRIGGER;
      run.window.counters.nightRetrigger = granted + B.NIGHT_SHIFT_RETRIGGER;
    }
    events.push({ e: 'NIGHT_SHIFT', left: run.nightShift.left, wild: run.nightShift.wild });
    if (run.nightShift.left <= 0) run.nightShift = null;
  }

  // 9. TRIGGERS: bonus sigils (a Grin counts as any sigil with Grinning Sigil).
  const grins = mods.grinIsSigil ? ev.view.all().filter((v) => v.def.wild).length : 0;
  const fresh = (mode: BonusMode) => (run.window.counters[`bonus_${mode}`] ?? 0) < B.BONUS_PER_WINDOW;
  for (const mode of mods.bonuses) {
    if (nightSpin && mode === 'wheel') continue;
    if (fresh(mode) && (ev.sigils[mode] ?? 0) + grins >= B.SIGILS_TO_TRIGGER) {
      startBonus(state, mods, mode, events);
      return;
    }
  }
  // Serendipity: a dead spin may open a random unlocked bonus.
  const open = [...mods.bonuses].filter(fresh) as BonusMode[];
  if (payout.lte(0) && mods.serendipity > 0 && open.length && rng.chance(mods.serendipity)) {
    startBonus(state, mods, rng.pick(open), events);
    return;
  }
  // Double or Devoured: a Big+ win may be staked on the ladder.
  if (mods.bonuses.has('hilo') && tierAtLeast(tier, 'big') && payout.gt(0)) {
    run.gamble = { stake: payout };
    run.phase = 'gamble';
    events.push({ e: 'GAMBLE_OFFER', stake: payout.toString() });
    say(state, 'gambleOffer', events, undefined, { force: true });
    return;
  }
  afterSpin(state, mods, events);
}

export function gambleAccept(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'gamble' || !run.gamble) return 'no gamble';
  const stake = run.gamble.stake;
  run.gamble = null;
  run.coins = run.coins.sub(stake).max(0);
  startBonus(state, mods, 'hilo', events, { stake });
  return undefined;
}

export function gambleDecline(state: GameState, mods: Modifiers, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || run.phase !== 'gamble') return 'no gamble';
  run.gamble = null;
  afterSpin(state, mods, events);
  return undefined;
}

function destroy(run: RunState, uid: number, by: string, events: GameEvent[]): boolean {
  const idx = run.deck.findIndex((d) => d.uid === uid);
  if (idx < 0 || run.deck.length <= 1) return false;
  const [inst] = run.deck.splice(idx, 1);
  removeFromStrips(run, uid);
  run.stats.destroyed++;
  events.push({ e: 'SYMBOL_DESTROYED', uid, defId: inst!.defId, by });
  return true;
}
